import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  RefreshCw,
  LogIn,
  Trash2,
  Users,
  MoreVertical,
  Edit,
  ChevronRight,
  Search,
  MessageCircle,
  Layers,
  Zap,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import AddShopModal from "../../components/AddShopModal";
import ShopGroupModal from "../../components/ShopGroupModal";
import shopApi from "../../../../api/shopApi.js";
import shopGroupApi from "../../../../api/shopGroupApi.js";
import LoadingIcon from "../../../../components/LoadingIcon/index.jsx";
import { getShopsRequest } from "../../slices/shopActions.js";
import { Link } from "react-router-dom";
import { useNotification } from "../../../../hooks/useNotification.jsx";
import { useIsMobile } from "../../../../hooks/useIsMobile.jsx";
import { getWebSocketUrl } from "../../../../api/websocket.js";

function IconBtn({ icon: Icon, title, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`p-1.5 rounded-md transition-colors ${
        danger
          ? "text-gray-500 hover:bg-red-50 hover:text-red-600"
          : "text-gray-500 hover:bg-blue-50 hover:text-blue-600"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

function TabButton({ active, onClick, children, count, countColor = "gray" }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium border-b-2 transition-colors whitespace-nowrap ${
        active
          ? "border-blue-600 text-blue-600"
          : "border-transparent text-gray-600 hover:text-gray-900"
      }`}
    >
      {children}
      {count !== undefined && (
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
            countColor === "green"
              ? "bg-green-100 text-green-700"
              : active
                ? "bg-blue-100 text-blue-700"
                : "bg-gray-100 text-gray-600"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function MessageGomToggle({ shop, disabled, isUpdating, onToggle }) {
  const enabled = Boolean(shop?.isMessageGomEnabled);
  const title = enabled ? "Tắt gom QA" : "Bật gom QA";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      title={title}
      disabled={disabled || isUpdating}
      onClick={() => onToggle(shop)}
      className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 disabled:opacity-60 flex-shrink-0"
    >
      <span>Gom QA</span>
      <span
        className={`relative inline-flex h-4 w-7 rounded-full transition-colors ${
          enabled ? "bg-blue-600" : "bg-gray-300"
        }`}
      >
        <span
          className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform ${
            enabled ? "translate-x-3.5" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}

function ActionMenu({ id, relogin, checkLoginStatus, deleteShop }) {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openUpwards, setOpenUpwards] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = (menuId) => {
    if (openMenuId === menuId) {
      setOpenMenuId(null);
    } else {
      setOpenMenuId(menuId);
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setOpenUpwards(window.innerHeight - rect.bottom < 200);
      }
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={() => toggleMenu(id)}
        className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
      >
        <MoreVertical className="h-4 w-4 text-gray-500" />
      </button>
      {openMenuId === id && (
        <div
          className={`absolute ${
            openUpwards ? "bottom-full mb-2" : "top-full mt-2"
          } right-0 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 min-w-[200px]`}
        >
          <div className="py-1" role="menu">
            <button
              className="flex items-center w-full px-3 py-2 text-[13px] text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
              onClick={() => {
                setOpenMenuId(null);
                checkLoginStatus();
              }}
            >
              <RefreshCw className="mr-2.5 h-4 w-4" />
              Kiểm tra trạng thái
            </button>
            <button
              className="flex items-center w-full px-3 py-2 text-[13px] text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
              onClick={() => {
                setOpenMenuId(null);
                relogin();
              }}
            >
              <LogIn className="mr-2.5 h-4 w-4" />
              Đăng nhập lại
            </button>
            <button
              className="flex items-center w-full px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors"
              onClick={() => {
                setOpenMenuId(null);
                deleteShop();
              }}
            >
              <Trash2 className="mr-2.5 h-4 w-4" />
              Xoá
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function GroupActionMenu({ onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
      >
        <MoreVertical className="h-4 w-4 text-gray-500" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 min-w-[140px]">
          <button
            className="flex items-center w-full px-3 py-2 text-[13px] text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          >
            <Edit className="mr-2.5 h-4 w-4" />
            Sửa
          </button>
          <button
            className="flex items-center w-full px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            <Trash2 className="mr-2.5 h-4 w-4" />
            Xoá
          </button>
        </div>
      )}
    </div>
  );
}

export default function ManageShopPage({ setIsLoadingScreen }) {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const auth = useSelector((state) => state.auth);
  const shops = useSelector((state) => state.shops);
  const { addNotification } = useNotification();

  const [isAddShopModalOpen, setIsAddShopModalOpen] = useState(false);
  const [isShopGroupModalOpen, setIsShopGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [shopGroups, setShopGroups] = useState([]);
  const [qrImgSrc, setQrImgSrc] = useState("");
  const [isLoadingAddShop, setIsLoadingAddShop] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [messageGomUpdatingIds, setMessageGomUpdatingIds] = useState(
    () => new Set(),
  );

  const fetchShopGroups = useCallback(async () => {
    if (!auth.user?._id) return;
    try {
      const res = await shopGroupApi.getAll(auth.user._id);
      if (res.status === 1) {
        setShopGroups(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching shop groups:", error);
    }
  }, [auth.user?._id]);

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setIsShopGroupModalOpen(true);
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("Bạn có muốn xoá nhóm này không?")) return;
    try {
      const res = await shopGroupApi.delete(groupId, auth.user._id);
      if (res.status === 1) {
        addNotification("Xoá nhóm thành công!", 1);
        fetchShopGroups();
      } else {
        addNotification(res.message, -1);
      }
    } catch (error) {
      addNotification("Có lỗi xảy ra", -1);
    }
  };

  const showActionColumn =
    auth.user?.role?.includes("super_admin") ||
    auth.user?.role?.includes("admin");

  const showAddShopButton = showActionColumn;

  const refreshShops = useCallback(() => {
    if (!auth.user?._id) return;
    dispatch(
      getShopsRequest({
        accountId: auth.user.accountId,
        staffId: auth.user._id,
      }),
    );
  }, [auth.user?._id, auth.user?.accountId, dispatch]);

  const handleLogin = async (action, shopId) => {
    try {
      setQrImgSrc("");
      setIsAddShopModalOpen(false);
      setIsLoadingAddShop(true);
      setIsLoadingScreen(true);
      const socket = new WebSocket(getWebSocketUrl());
      let finishedLogin = false;
      let qrReceived = false;
      // Watchdog: nếu đã quét QR (type 4) nhưng sau 90s vẫn không nhận được
      // type 5 / "logged_in" → báo lỗi và đóng modal để không treo UI.
      let loginWatchdog = null;
      let connectWatchdog = null;
      const clearLoginWatchdog = () => {
        if (loginWatchdog) {
          clearTimeout(loginWatchdog);
          loginWatchdog = null;
        }
      };
      const clearConnectWatchdog = () => {
        if (connectWatchdog) {
          clearTimeout(connectWatchdog);
          connectWatchdog = null;
        }
      };
      // QR watchdog: nếu không nhận được type 0 (QR image) trong 30s sau khi
      // gửi login_qr → server có thể đã chết silent; tắt spinner để user retry.
      let qrWatchdog = null;
      const clearQrWatchdog = () => {
        if (qrWatchdog) {
          clearTimeout(qrWatchdog);
          qrWatchdog = null;
        }
      };
      const closeSocket = () => {
        try {
          if (
            socket.readyState === WebSocket.CONNECTING ||
            socket.readyState === WebSocket.OPEN
          ) {
            socket.close();
          }
        } catch (_) {}
      };
      const resetLoginUi = () => {
        setIsAddShopModalOpen(false);
        setIsLoadingAddShop(false);
        setIsLoadingScreen(false);
      };
      const finishWithError = (message) => {
        if (finishedLogin) return;
        finishedLogin = true;
        clearConnectWatchdog();
        clearLoginWatchdog();
        clearQrWatchdog();
        addNotification(
          message || "Không thể đăng nhập Zalo, vui lòng thử lại",
          -1,
        );
        resetLoginUi();
        closeSocket();
      };
      const finishSuccessfully = () => {
        finishedLogin = true;
        clearConnectWatchdog();
        clearLoginWatchdog();
        clearQrWatchdog();
        resetLoginUi();
        closeSocket();
      };
      connectWatchdog = setTimeout(() => {
        finishWithError("Không kết nối được máy chủ tạo QR, vui lòng thử lại");
      }, 10_000);
      socket.onopen = () => {
        clearConnectWatchdog();
        try {
          socket.send(
            JSON.stringify({
              type: "login_qr",
              shopId: shopId,
              action,
              accountId: auth.user.accountId,
              staffId: auth.user._id,
            }),
          );
        } catch (error) {
          finishWithError("Không gửi được yêu cầu tạo QR, vui lòng thử lại");
          return;
        }
        qrWatchdog = setTimeout(() => {
          finishWithError("Không nhận được mã QR, vui lòng thử lại");
        }, 30_000);
      };

      socket.onmessage = async (event) => {
        if (finishedLogin) return;
        let message;
        try {
          message = JSON.parse(event.data);
        } catch (error) {
          finishWithError("Dữ liệu đăng nhập không hợp lệ, vui lòng thử lại");
          return;
        }
        if (message.type === -1) {
          finishWithError(
            typeof message.data === "string"
              ? message.data
              : "Mã QR hết hạn",
          );
        } else if (message.type === 0) {
          const qrImage = message.data?.image;
          if (!qrImage) {
            finishWithError("Không nhận được dữ liệu mã QR, vui lòng thử lại");
            return;
          }
          qrReceived = true;
          clearQrWatchdog();
          setIsLoadingAddShop(false);
          setIsLoadingScreen(false);
          setQrImgSrc("data:image/png;base64," + qrImage);
          setIsAddShopModalOpen(true);
        } else if (message.type === 2) {
          addNotification("Vui lòng xác nhận trên điện thoại của bạn", 1);
          setIsLoadingAddShop(false);
          setIsLoadingScreen(true);
        } else if (message.type === 4) {
          clearLoginWatchdog();
          loginWatchdog = setTimeout(() => {
            finishWithError("Đăng nhập quá hạn, vui lòng thử lại");
          }, 90_000);
        } else if (message.type === 5) {
          clearLoginWatchdog();
          clearQrWatchdog();
          setIsLoadingAddShop(false);
          setIsLoadingScreen(true);
          const committedShop = message.data?.shop || null;
          const committedShopId = committedShop?._id || committedShop?.id;
          if (action === "login_new") {
            const shop = shops.data.find((s) => s.zaloId === message.data.uid);
            if (shop && !committedShopId) {
              finishWithError(
                "Tài khoản này của bạn đã có trong hệ thống, vui lòng đăng nhập lại chính xác tài khoản",
              );
            } else {
              addNotification("Đăng nhập thành công", 1);
              try {
                if (!committedShopId) {
                  throw new Error("Gateway chưa trả về shop đã đăng nhập");
                }
                refreshShops();
                socket.send(
                  JSON.stringify({
                    type: "listen_message",
                    accountId: auth.user.accountId,
                    staffId: auth.user._id,
                    shopStaffId: auth.user._id,
                    shopId: committedShopId,
                    zaloId: message.data.uid,
                    sessionRevision: committedShop.sessionRevision ?? 0,
                  }),
                );
              } catch (error) {
                const responseData = error?.response?.data;
                refreshShops();
                finishWithError(
                  responseData?.message || "Không thể kích hoạt tài khoản Zalo",
                );
              }
            }
          } else if (action === "relogin") {
            const shop = shops.data.find((s) => s._id === shopId);
            if (!shop || shop.zaloId !== message.data.uid) {
              finishWithError(
                "Đây không phải là Zalo đang sử dụng, bạn muốn thêm 1 Zalo mới, vui lòng thêm tài khoản",
              );
            } else {
              addNotification("Đăng nhập thành công", 1);
              try {
                if (!committedShopId) {
                  throw new Error("Gateway chưa trả về shop đã đăng nhập");
                }
                refreshShops();
                socket.send(
                  JSON.stringify({
                    type: "listen_message",
                    accountId: auth.user.accountId,
                    staffId: auth.user._id,
                    shopStaffId: shop.staffId,
                    shopId: shopId,
                    zaloId: message.data.uid,
                    sessionRevision: committedShop.sessionRevision ?? 0,
                  }),
                );
              } catch (error) {
                const responseData = error?.response?.data;
                refreshShops();
                finishWithError(
                  responseData?.message || "Không thể kích hoạt tài khoản Zalo",
                );
              }
            }
          }
        } else if (message.type === "logged_in") {
          finishSuccessfully();
        } else if (
          message.type === "logged_out" ||
          message.type === "duplicate_zalo_active" ||
          message.type === "session_stale"
        ) {
          finishWithError("Không thể kết nối tài khoản Zalo, vui lòng thử lại");
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
        finishWithError("Lỗi kết nối máy chủ, vui lòng thử lại");
      };

      socket.onclose = () => {
        clearConnectWatchdog();
        clearLoginWatchdog();
        clearQrWatchdog();
        if (!finishedLogin) {
          finishedLogin = true;
          resetLoginUi();
          addNotification(
            qrReceived
              ? "Kết nối đăng nhập bị ngắt, vui lòng thử lại"
              : "Không nhận được mã QR, vui lòng thử lại",
            -1,
          );
        }
      };
    } catch (error) {
      addNotification("Không thể mở kết nối đăng nhập, vui lòng thử lại", -1);
      setIsLoadingAddShop(false);
      setIsLoadingScreen(false);
    }
  };

  const handleCheckLoginStatus2 = async (shopId) => {
    try {
      setIsLoadingScreen(true);
      const res = await shopApi.checkLoginStatus({
        shopId,
      });
      setIsLoadingScreen(false);
      if (res.status !== -2) {
        dispatch(
          getShopsRequest({
            accountId: auth.user.accountId,
            staffId: auth.user._id,
          }),
        );
        addNotification(res.message, res.status);
      } else addNotification("Có lỗi xảy ra", -1);
    } catch (error) {
      setIsLoadingScreen(false);
      addNotification(error, -1);
    }
  };

  const handleDelete = async (shopId) => {
    try {
      if (!window.confirm("Bạn có muốn xoá tài khoản này không?")) return;
      setIsLoadingScreen(true);
      const res = await shopApi.delete({
        shopId,
      });
      setIsLoadingScreen(false);
      if (res.status === 1) {
        dispatch(
          getShopsRequest({
            accountId: auth.user.accountId,
            staffId: auth.user._id,
          }),
        );
        addNotification("Xoá tài khoản thành công!", res.status);
      } else addNotification(res.message, res.status);
    } catch (error) {
      setIsLoadingScreen(false);
      addNotification(error, -1);
    }
  };

  const handleToggleMessageGom = async (shop) => {
    const shopId = shop?._id || shop?.id;
    if (!shopId) return;

    const nextEnabled = !Boolean(shop.isMessageGomEnabled);
    setMessageGomUpdatingIds((prev) => new Set(prev).add(shopId));
    try {
      const res = await shopApi.updateShop(shopId, {
        isMessageGomEnabled: nextEnabled,
      });
      if (res.status === 1) {
        addNotification(nextEnabled ? "Đã bật gom QA" : "Đã tắt gom QA", 1);
        refreshShops();
      } else {
        addNotification(res.message || "Không cập nhật được gom QA", -1);
      }
    } catch (error) {
      addNotification(
        error?.response?.data?.message || "Không cập nhật được gom QA",
        -1,
      );
    } finally {
      setMessageGomUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(shopId);
        return next;
      });
    }
  };

  useEffect(() => {
    if (auth.user._id && !isAddShopModalOpen) {
      dispatch(
        getShopsRequest({
          accountId: auth.user.accountId,
          staffId: auth.user._id,
        }),
      );
    }
  }, [dispatch, auth, isAddShopModalOpen]);

  useEffect(() => {
    fetchShopGroups();
  }, [fetchShopGroups]);

  const onlineCount = useMemo(
    () => shops.data.filter((s) => s.isLogin).length,
    [shops.data],
  );

  const filteredShops = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return shops.data
      .filter((s) => (activeTab === "online" ? s.isLogin : true))
      .filter((s) => (q ? s.name?.toLowerCase().includes(q) : true));
  }, [shops.data, activeTab, searchQuery]);

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return shopGroups.filter((g) =>
      q ? g.name?.toLowerCase().includes(q) : true,
    );
  }, [shopGroups, searchQuery]);

  const showSearch = shops.data.length > 8 || shopGroups.length > 8;

  const renderShopRowDesktop = (account, i) => (
    <div
      key={account._id}
      className="group flex items-center gap-3 px-3 py-2 border-b border-gray-100 hover:bg-blue-50/60 transition-colors"
    >
      <span className="text-[11px] text-gray-400 w-6 text-right flex-shrink-0">
        {i + 1}
      </span>
      <div className="relative flex-shrink-0">
        <img
          className="h-8 w-8 rounded-full object-cover"
          src={account.avatar}
          alt=""
        />
        <span
          className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${
            account.isLogin ? "bg-green-500" : "bg-gray-400"
          }`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-gray-900 truncate">
          {account.name}
        </div>
        <div className="text-[11px] text-gray-500 truncate">
          {auth.user.name}
        </div>
      </div>
      <span
        className={`hidden lg:inline-flex text-[11px] px-2 py-0.5 rounded-full font-medium ${
          account.isLogin
            ? "bg-green-50 text-green-700"
            : "bg-gray-100 text-gray-500"
        }`}
      >
        {account.isLogin ? "Đã đăng nhập" : "Chưa đăng nhập"}
      </span>
      {showActionColumn && (
        <MessageGomToggle
          shop={account}
          disabled={!showActionColumn}
          isUpdating={messageGomUpdatingIds.has(account._id)}
          onToggle={handleToggleMessageGom}
        />
      )}
      {showActionColumn && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <IconBtn
            icon={RefreshCw}
            title="Kiểm tra trạng thái"
            onClick={() => handleCheckLoginStatus2(account._id)}
          />
          <IconBtn
            icon={LogIn}
            title="Đăng nhập lại"
            onClick={() => handleLogin("relogin", account._id)}
          />
          <IconBtn
            icon={Trash2}
            title="Xoá"
            danger
            onClick={() => handleDelete(account._id)}
          />
        </div>
      )}
      <Link
        to={`/chat/${account._id}`}
        className="text-[12px] font-medium bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white px-2.5 py-1 rounded-md flex items-center gap-1 transition-colors flex-shrink-0"
      >
        Mở chat
        <ChevronRight className="h-3 w-3" />
      </Link>
    </div>
  );

  const renderShopCardMobile = (account, i) => (
    <div
      key={account._id}
      className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm"
    >
      <div className="flex items-start gap-2.5">
        <span className="text-[11px] text-gray-400 pt-1 flex-shrink-0">
          {i + 1}
        </span>
        <div className="relative flex-shrink-0">
          <img
            className="h-9 w-9 rounded-full object-cover"
            src={account.avatar}
            alt=""
          />
          <span
            className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${
              account.isLogin ? "bg-green-500" : "bg-gray-400"
            }`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-gray-900 truncate">
            {account.name}
          </div>
          <div className="text-[11px] text-gray-500 truncate">
            {auth.user.name}
          </div>
        </div>
        {showActionColumn && (
          <ActionMenu
            id={account._id}
            relogin={() => handleLogin("relogin", account._id)}
            checkLoginStatus={() => handleCheckLoginStatus2(account._id)}
            deleteShop={() => handleDelete(account._id)}
          />
        )}
      </div>
      <div className="flex items-center justify-between gap-2 mt-2.5 pt-2.5 border-t border-gray-50">
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
            account.isLogin
              ? "bg-green-50 text-green-700"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {account.isLogin ? "Đã đăng nhập" : "Chưa đăng nhập"}
        </span>
        {showActionColumn && (
          <MessageGomToggle
            shop={account}
            disabled={!showActionColumn}
            isUpdating={messageGomUpdatingIds.has(account._id)}
            onToggle={handleToggleMessageGom}
          />
        )}
        <Link
          to={`/chat/${account._id}`}
          className="text-[12px] text-blue-600 font-medium flex items-center gap-1 hover:text-blue-700"
        >
          Mở chat
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );

  const renderGroupCard = (group) => (
    <div
      key={group.id}
      className="group bg-white border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-center gap-2.5 mb-2">
        <div className="h-9 w-9 rounded-lg overflow-hidden bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center flex-shrink-0">
          {group.avatar ? (
            <img
              src={group.avatar}
              className="w-full h-full object-cover"
              alt=""
            />
          ) : (
            <Layers className="h-4 w-4 text-white" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-gray-900 truncate">
            {group.name}
          </div>
          <div className="text-[11px] text-gray-500">
            {group.shopIds?.length || 0} Zalo
          </div>
        </div>
        {showActionColumn && (
          <GroupActionMenu
            onEdit={() => handleEditGroup(group)}
            onDelete={() => handleDeleteGroup(group.id)}
          />
        )}
      </div>
      <div className="flex items-center -space-x-1.5 mb-2.5 min-h-[20px]">
        {shops.data
          .filter((s) => group.shopIds?.includes(s._id))
          .slice(0, 4)
          .map((s) => (
            <img
              key={s._id}
              src={s.avatar}
              alt={s.name}
              title={s.name}
              className="h-5 w-5 rounded-full ring-2 ring-white object-cover"
            />
          ))}
        {(group.shopIds?.length || 0) > 4 && (
          <span className="text-[10px] text-gray-500 ml-2">
            +{group.shopIds.length - 4}
          </span>
        )}
      </div>
      <Link
        to={`/chat/group/${group.id}`}
        className="text-[12px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
      >
        Mở chat nhóm
        <ChevronRight className="h-3 w-3" />
      </Link>
    </div>
  );

  const renderEmptyShops = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="h-14 w-14 rounded-full bg-blue-50 flex items-center justify-center mb-3">
        <MessageCircle className="h-7 w-7 text-blue-500" />
      </div>
      <h3 className="text-[13px] font-semibold text-gray-900">
        Chưa có Zalo nào
      </h3>
      <p className="text-[12px] text-gray-500 mt-1 mb-4 max-w-xs">
        Thêm tài khoản Zalo đầu tiên để bắt đầu quản lý hội thoại
      </p>
      {showAddShopButton && (
        <button
          onClick={() => handleLogin("login_new")}
          disabled={isLoadingAddShop}
          className="text-[13px] bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors"
        >
          {isLoadingAddShop ? (
            <LoadingIcon className="w-3.5 h-3.5" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          Thêm Zalo ngay
        </button>
      )}
    </div>
  );

  const renderEmptyGroups = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="h-14 w-14 rounded-full bg-purple-50 flex items-center justify-center mb-3">
        <Users className="h-7 w-7 text-purple-500" />
      </div>
      <h3 className="text-[13px] font-semibold text-gray-900">
        Chưa có nhóm Zalo nào
      </h3>
      <p className="text-[12px] text-gray-500 mt-1 mb-4 max-w-xs">
        Gom các Zalo thành nhóm để quản lý và chat tập trung
      </p>
      {showAddShopButton && (
        <button
          onClick={() => setIsShopGroupModalOpen(true)}
          className="text-[13px] bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors"
        >
          <Users className="h-3.5 w-3.5" />
          Tạo nhóm đầu tiên
        </button>
      )}
    </div>
  );

  const renderEmptySearch = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
        <Search className="h-5 w-5 text-gray-400" />
      </div>
      <p className="text-[13px] text-gray-600">
        Không tìm thấy kết quả cho "{searchQuery}"
      </p>
      <button
        onClick={() => setSearchQuery("")}
        className="text-[12px] text-blue-600 hover:text-blue-700 mt-2"
      >
        Xoá từ khoá
      </button>
    </div>
  );

  const renderContent = () => {
    if (activeTab === "groups") {
      if (shopGroups.length === 0) return renderEmptyGroups();
      if (filteredGroups.length === 0 && searchQuery) return renderEmptySearch();
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredGroups.map(renderGroupCard)}
        </div>
      );
    }

    if (shops.data.length === 0) return renderEmptyShops();
    if (filteredShops.length === 0) {
      if (searchQuery) return renderEmptySearch();
      if (activeTab === "online") {
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <Zap className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-[13px] text-gray-600">
              Không có Zalo nào đang online
            </p>
          </div>
        );
      }
    }

    if (isMobile) {
      return (
        <div className="space-y-2.5">
          {filteredShops.map((account, i) => renderShopCardMobile(account, i))}
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredShops.map((account, i) => renderShopRowDesktop(account, i))}
      </div>
    );
  };

  return (
    <>
      <div className="flex-1 overflow-auto bg-slate-50">
        {/* Sticky Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-gray-200 z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-5 py-3 gap-3">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="h-4 w-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-[15px] font-semibold text-gray-900 leading-tight">
                  Quản lý Zalo
                </h1>
                <p className="text-[11px] text-gray-500">
                  {shops.data.length} tài khoản • {shopGroups.length} nhóm
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {showSearch && (
                <div className="relative flex-1 md:flex-none">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Tìm Zalo / nhóm..."
                    className="pl-8 pr-3 py-1.5 text-[13px] border border-gray-300 rounded-md w-full md:w-56 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                  />
                </div>
              )}
              {showAddShopButton && (
                <>
                  <button
                    className="text-[13px] bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors"
                    onClick={() => handleLogin("login_new")}
                    disabled={isLoadingAddShop}
                  >
                    {isLoadingAddShop ? (
                      <LoadingIcon className="w-3.5 h-3.5" />
                    ) : (
                      <Plus className="h-3.5 w-3.5" />
                    )}
                    Thêm Zalo
                  </button>
                  <button
                    className="text-[13px] bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors"
                    onClick={() => setIsShopGroupModalOpen(true)}
                  >
                    <Users className="h-3.5 w-3.5" />
                    Tạo nhóm
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-3 md:px-5 overflow-x-auto">
            <TabButton
              active={activeTab === "all"}
              onClick={() => setActiveTab("all")}
              count={shops.data.length}
            >
              Tất cả
            </TabButton>
            <TabButton
              active={activeTab === "online"}
              onClick={() => setActiveTab("online")}
              count={onlineCount}
              countColor="green"
            >
              Đang online
            </TabButton>
            <TabButton
              active={activeTab === "groups"}
              onClick={() => setActiveTab("groups")}
              count={shopGroups.length}
            >
              Nhóm
            </TabButton>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 md:p-5">{renderContent()}</div>
      </div>

      <AddShopModal
        isOpen={isAddShopModalOpen}
        closeModal={() => setIsAddShopModalOpen(false)}
        qrImgSrc={qrImgSrc}
      />
      <ShopGroupModal
        isOpen={isShopGroupModalOpen}
        closeModal={() => {
          setIsShopGroupModalOpen(false);
          setEditingGroup(null);
        }}
        shops={shops.data}
        staffId={auth.user._id}
        editGroup={editingGroup}
        onSaved={() => {
          fetchShopGroups();
          setEditingGroup(null);
        }}
      />
    </>
  );
}
